import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Input, Empty, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import _ from 'lodash';
import {
  QuickPickRow,
  PickerBox,
  ValueRow,
  ValueHead,
  ValuePartners,
  PickerFooter,
  LinkButton,
} from './style';

const SEARCHABLE_FROM = 8
const NAMES_SHOWN = 2
// ตอนยังไม่มีข้อมูล ไม่ต้องกันที่เต็มความสูง กล่องเปล่าใหญ่ ๆ ทำให้คอลัมน์โหว่
const EMPTY_HEIGHT = 110

// Custom control สำหรับค่าที่ HR แต่ละที่กรอกเอง (salary_date / employment_status)
// ค่าซ้ำ ๆ กันหลายสิบแบบ จึงต้องบอกให้ได้ว่าแต่ละค่ามาจากบริษัทไหน และกระทบกี่คน
const ValuePicker = ({ options, partners, loading, height, emptyText, value, onChange }) => {
  const [keyword, setKeyword] = React.useState('')

  const selectedValues = value || []
  const selected = React.useMemo(() => new Set(selectedValues), [selectedValues])
  const partnersById = React.useMemo(() => _.keyBy(partners, 'id'), [partners])

  React.useEffect(() => { setKeyword('') }, [options])

  const visibleOptions = React.useMemo(() => {
    const kw = keyword.trim().toLowerCase()

    return kw ? options.filter(({ value: val }) => `${val}`.toLowerCase().includes(kw)) : options
  }, [options, keyword])

  const partnerNames = (partnerIds) => _.compact(
    _.uniq(partnerIds).map((id) => _.get(partnersById, [id, 'name']))
  )

  const toggle = (val, checked) => {
    onChange(checked ? _.union(selectedValues, [val]) : _.without(selectedValues, val))
  }

  if (options.length === 0) {
    return (
      <PickerBox $height={EMPTY_HEIGHT} $empty>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loading ? 'กำลังโหลด...' : emptyText} />
      </PickerBox>
    );
  }

  return (
    <>
      {options.length >= SEARCHABLE_FROM &&
        <QuickPickRow>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="ค้นหา"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </QuickPickRow>
      }

      <PickerBox $height={height}>
        {visibleOptions.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ไม่พบค่าที่ค้นหา" />}
        {visibleOptions.map(({ value: val, partner_ids }) => {
          const names = partnerNames(partner_ids)
          const shown = names.slice(0, NAMES_SHOWN).join(', ')
          const rest = names.length - NAMES_SHOWN

          return (
            <ValueRow key={`value-${val}`}>
              <ValueHead>
                <Checkbox checked={selected.has(val)} onChange={(e) => toggle(val, e.target.checked)}>
                  {val || '(ไม่ระบุ)'}
                </Checkbox>
              </ValueHead>
              <Tooltip title={names.join(', ')}>
                <ValuePartners>{rest > 0 ? `${shown} +${rest}` : shown}</ValuePartners>
              </Tooltip>
            </ValueRow>
          )
        })}
      </PickerBox>

      <PickerFooter>
        <span>{`เลือกแล้ว ${selectedValues.length} / ${options.length}`}</span>
        <span>
          <LinkButton onClick={() => onChange(_.union(selectedValues, _.map(visibleOptions, 'value')))}>
            {keyword ? 'เลือกทั้งหมดที่ค้นเจอ' : 'เลือกทั้งหมด'}
          </LinkButton>
          <LinkButton onClick={() => onChange([])} disabled={selectedValues.length === 0}>ล้างทั้งหมด</LinkButton>
        </span>
      </PickerFooter>
    </>
  );
}

ValuePicker.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    partner_ids: PropTypes.arrayOf(PropTypes.number),
  })).isRequired,
  partners: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  height: PropTypes.number,
  emptyText: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
}

export default ValuePicker
